type BytesInput = number | string;

interface FormatOptions {
  decimals?: number;
}

/**
 * Converts a byte value into a human-readable format, using binary (kibibyte-based) units.
 *
 * @param {number | string} bytes - The number of bytes to format. Can be a number or a string representing a number.
 * @param {number} decimals - (Optional) The number of decimal places to include in the formatted output. Defaults to 2.
 *
 * @returns {string} A string representing the formatted bytes in the appropriate unit (Bytes, KiB, MiB, GiB, TiB, PiB, EiB, ZiB, YiB).
 * If the input is invalid (e.g., a non-numeric string), returns '0 Bytes'.
 *
 * @example
 * // Returns '1.00 KiB'
 * formatBytes(1024);
 *
 * @example
 * // Returns '1.21 KiB'
 * formatBytes('1234');
 *
 * @example
 * // Returns '1.205 KiB'
 * formatBytes(1234, 3);
 *
 * @example
 * // Returns '0 Bytes' for invalid input
 * formatBytes('not a number');
 */
export const formatBytes = (bytes: BytesInput, { decimals = 2 }: FormatOptions = {}): string => {
  const bytesNumber = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;

  if (isNaN(bytesNumber) || bytesNumber === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  const i = Math.floor(Math.log(bytesNumber) / Math.log(k));

  return parseFloat((bytesNumber / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};
